import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import DateField from "../DateField";
import TemplateThumbnail from "../TemplateThumbnail";
import TemplatedResumePreview from "../TemplatedResumePreview";
import { ArrowLeft, ArrowRight, Loader2, Plus, Trash2, Sparkles, RefreshCw, Check, Pencil, FileText, LayoutTemplate } from "lucide-react";

const SECTIONS = [
  { id: "target", label: "Target role" },
  { id: "about", label: "About you" },
  { id: "experience", label: "Experience" },
  { id: "education", label: "Education" },
  { id: "skills", label: "Skills" },
  { id: "extras", label: "Extras" },
  { id: "review", label: "Review & build" },
];

const emptyPosition = () => ({ company: "", title: "", start_date: "", end_date: "", is_current: false, location: "", achievements: ["", ""] });
const listToText = (arr) => (arr || []).join("\n");

const Field = ({ label, hint, children, testId }) => (
  <label className="flex h-full flex-col" data-testid={testId}>
    <span className="text-[11px] font-mono uppercase tracking-wider text-slate-500">{label}</span>
    <div className="mt-1.5 flex-1 flex flex-col justify-end">{children}</div>
    {hint && <span className="mt-1 block text-[11px] text-slate-400">{hint}</span>}
  </label>
);

const inputCls = "h-11 rounded-xl bg-white border-slate-200 text-slate-900";
const selectCls = "h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-200";
const textareaCls = "rounded-xl bg-white border-slate-200 text-slate-900";

const Chips = ({ items, onRemove, testId }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={testId}>
    {items.map((s) => (
      <span key={s} className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-xs font-medium text-sky-700">
        {s}
        <button onClick={() => onRemove(s)} className="text-sky-500 hover:text-rose-500" aria-label={`Remove ${s}`}>×</button>
      </span>
    ))}
  </div>
);

const AUTOSAVE_KEY = "tj-builder-autosave-v1";

const ResumeBuilderWizard = ({ onDone, onExit }) => {
  const [cfg, setCfg] = useState(null);
  const [idx, setIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const [regensLeft, setRegensLeft] = useState(null);
  const [skillInput, setSkillInput] = useState("");
  const [raw, setRaw] = useState({});
  const [templates, setTemplates] = useState([]);
  const [restorable, setRestorable] = useState(null);
  const [savedAt, setSavedAt] = useState("");
  const [a, setA] = useState({
    full_name: "", email: "", phone: "", city: "", linkedin: "",
    target_role_id: "", target_role_other: "", seniority: "", years_experience: 0,
    work_model: "No preference", preferred_locations: "", current_ctc: "", expected_ctc: "",
    ctc_currency: "INR", notice_period: "", positions: [emptyPosition()],
    education: [], certifications: [], skills: [], tools: [], languages: [], awards: [],
    extra_notes: "", preferred_template: "modern",
  });

  useEffect(() => { api.get("/builder/config").then((r) => setCfg(r.data)).catch(() => toast.error("Could not load the builder")); }, []);

  const hasContent = (answers) => !!(answers && (String(answers.full_name || "").trim() || answers.target_role_id ||
    String(answers.seniority || "").trim() || Number(answers.years_experience) > 0 ||
    String(answers.current_ctc || "").trim() || String(answers.expected_ctc || "").trim() ||
    String(answers.preferred_locations || "").trim() || String(answers.extra_notes || "").trim() ||
    (answers.positions || []).some((p) => (p.company || "").trim() || (p.title || "").trim()) ||
    (answers.skills || []).length || (answers.education || []).join("").trim()));

  // Autosave: nobody loses a half-finished questionnaire.
  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(AUTOSAVE_KEY) || "null");
      if (hasContent(stored?.answers)) setRestorable(stored);
    } catch { /* ignore a corrupt autosave */ }
  }, []);

  useEffect(() => {
    if (restorable || !hasContent(a)) return; // nothing worth saving yet, and never overwrite a pending restore offer
    const timer = setTimeout(() => {
      try {
        localStorage.setItem(AUTOSAVE_KEY, JSON.stringify({ answers: a, raw, step: idx, saved_at: new Date().toISOString() }));
        setSavedAt(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
      } catch { /* storage full or blocked */ }
    }, 800);
    return () => clearTimeout(timer);
  }, [a, raw, idx, restorable]);

  const restore = () => {
    setA((prev) => ({ ...prev, ...restorable.answers }));
    setRaw(restorable.raw || {});
    setIdx(Math.min(restorable.step || 0, SECTIONS.length - 1));
    setRestorable(null);
    toast.success("Your saved answers are back");
  };

  const discardSaved = () => {
    localStorage.removeItem(AUTOSAVE_KEY);
    setRestorable(null);
  };

  useEffect(() => {
    api.get("/templates").then((r) => {
      const list = r.data.map((t) => ({ ...t, serif: t.font === "serif", nameAlign: t.name_align }));
      setTemplates(list);
      const preferred = (list.find((t) => t.layout === "single") || list[0])?.id;
      if (preferred) setA((prev) => ({ ...prev, preferred_template: preferred }));
    }).catch(() => {});
  }, []);

  const set = (k, v) => setA((prev) => ({ ...prev, [k]: v }));
  const setPos = (i, k, v) => setA((prev) => ({ ...prev, positions: prev.positions.map((p, j) => (j === i ? { ...p, [k]: v } : p)) }));

  // Keep the raw typed text so spaces and blank lines survive while typing; clean only on submit.
  const lineField = (key) => ({
    value: raw[key] ?? listToText(a[key]),
    onChange: (e) => {
      setRaw((r) => ({ ...r, [key]: e.target.value }));
      set(key, e.target.value.split("\n"));
    },
  });
  const cleanList = (arr) => (arr || []).map((x) => x.trim()).filter(Boolean);

  const selectedRole = useMemo(() => (cfg?.roles || []).find((r) => r.id === a.target_role_id), [cfg, a.target_role_id]);
  const suggestedSkills = useMemo(() => {
    const pool = [...(selectedRole?.required_skills || []), ...(selectedRole?.preferred_skills || [])];
    return pool.filter((s) => !a.skills.includes(s)).slice(0, 14);
  }, [selectedRole, a.skills]);

  const section = SECTIONS[idx];
  const canNext = () => {
    if (section.id === "target") return !!(a.target_role_id && (a.target_role_id !== "other" || a.target_role_other.trim()));
    if (section.id === "about") return !!a.full_name.trim();
    if (section.id === "experience") return a.positions.some((p) => p.company.trim() && p.title.trim());
    return true;
  };

  const build = async () => {
    setBusy(true);
    try {
      const payload = {
        ...a,
        education: cleanList(a.education), certifications: cleanList(a.certifications),
        tools: cleanList(a.tools), languages: cleanList(a.languages), awards: cleanList(a.awards),
        positions: a.positions.map((p) => ({ ...p, achievements: cleanList(p.achievements) })),
      };
      const { data } = await api.post("/builder/drafts", payload);
      setDraft(data.draft);
      setRegensLeft(data.regenerations_left);
      toast.success("Your resume is drafted — review and edit anything");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build your resume");
    } finally { setBusy(false); }
  };

  const regenerate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/builder/drafts/${draft.id}/regenerate`);
      setDraft(data.draft);
      setRegensLeft(data.regenerations_left);
      toast.success(`Rewritten — ${data.regenerations_left} AI rewrite${data.regenerations_left === 1 ? "" : "s"} left`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rewrite failed");
    } finally { setBusy(false); }
  };

  const saveEdits = async (resume) => {
    setDraft((d) => ({ ...d, resume }));
    await api.put(`/builder/drafts/${draft.id}`, { resume, preferred_template: a.preferred_template }).catch(() => toast.error("Could not save your edit"));
  };

  const finalize = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/builder/drafts/${draft.id}/finalize`);
      localStorage.removeItem(AUTOSAVE_KEY);
      toast.success("Resume saved — opening your Career Intelligence");
      onDone(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not finish your resume");
    } finally { setBusy(false); }
  };

  if (!cfg) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-sky-600" /></div>;

  const res = draft?.resume;
  const activeTpl = templates.find((t) => t.id === a.preferred_template) || templates[0];
  const pickTemplate = (id) => {
    set("preferred_template", id);
    if (draft) api.put(`/builder/drafts/${draft.id}`, { resume: draft.resume, preferred_template: id }).catch(() => {});
  };

  return (
    <div className="space-y-4" data-testid="resume-builder-wizard">
      {restorable && (
        <div className="accent-bar card-soft p-4 pl-5" style={{ "--accent": "#0D9488" }} data-testid="builder-restore-banner">
          <p className="text-sm font-semibold text-slate-900">We saved your earlier answers</p>
          <p className="mt-1 text-xs text-slate-600">
            You left this questionnaire part-way through{restorable.saved_at ? ` on ${new Date(restorable.saved_at).toLocaleString()}` : ""}. Pick up where you stopped, or start fresh.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button onClick={restore} className="h-10 rounded-xl bg-sky-600 hover:bg-sky-700 text-white" data-testid="builder-restore-button">Continue where I left off</Button>
            <Button onClick={discardSaved} variant="outline" className="h-10 rounded-xl border-slate-200 text-slate-700" data-testid="builder-discard-button">Start fresh</Button>
          </div>
        </div>
      )}
      <div className="card-soft p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold tracking-tight text-slate-900">Build your resume from scratch</h2>
            <p className="mt-1 text-sm text-slate-600">Answer a few questions. We write it, you edit it, then it lands in your Career Intelligence.</p>
          </div>
          <button onClick={onExit} className="shrink-0 text-sm text-sky-700 hover:text-sky-800" data-testid="builder-exit">Back</button>
        </div>
        {savedAt && !restorable && (
          <p className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-slate-500" data-testid="builder-autosave-status">
            <Check className="h-3 w-3 text-emerald-600" /> Answers saved automatically at {savedAt}
          </p>
        )}
        <div className="mt-4 flex flex-wrap gap-1.5" data-testid="builder-steps">
          {SECTIONS.map((s, i) => (
            <button key={s.id} onClick={() => i <= idx && setIdx(i)}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${i === idx ? "border-sky-300 bg-sky-50 text-sky-700" : i < idx ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-400"}`}
              data-testid={`builder-step-${s.id}`}>
              {i < idx ? <Check className="mr-1 inline h-3 w-3" /> : <span className="mr-1 font-mono">{i + 1}</span>}{s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="card-soft p-5 space-y-5 rise-in" key={section.id}>
        {section.id === "target" && (
          <>
            <Field label="Which role are you targeting?" hint="Pick from our role library — the resume is written against it." testId="builder-field-role">
              <select value={a.target_role_id} onChange={(e) => set("target_role_id", e.target.value)} className={selectCls} data-testid="builder-role-select">
                <option value="">Choose a role…</option>
                {cfg.roles.map((r) => <option key={r.id} value={r.id}>{r.title} · {r.category}</option>)}
                <option value="other">Other (type it myself)</option>
              </select>
            </Field>
            {a.target_role_id === "other" && (
              <Field label="Your target role title" testId="builder-field-role-other">
                <Input value={a.target_role_other} onChange={(e) => set("target_role_other", e.target.value)} placeholder="e.g. Head of Cloud Engineering" className={inputCls} data-testid="builder-role-other-input" />
              </Field>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Current seniority" testId="builder-field-seniority">
                <select value={a.seniority} onChange={(e) => set("seniority", e.target.value)} className={selectCls} data-testid="builder-seniority-select">
                  <option value="">Choose…</option>
                  {cfg.seniority_options.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Total years of experience" testId="builder-field-years">
                <Input type="number" min={0} max={60} value={a.years_experience} onChange={(e) => set("years_experience", Number(e.target.value))} className={inputCls} data-testid="builder-years-input" />
              </Field>
              <Field label="Preferred working model" testId="builder-field-work-model">
                <select value={a.work_model} onChange={(e) => set("work_model", e.target.value)} className={selectCls} data-testid="builder-work-model-select">
                  {cfg.work_models.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Preferred locations (optional)" testId="builder-field-locations">
                <Input value={a.preferred_locations} onChange={(e) => set("preferred_locations", e.target.value)} placeholder="e.g. Bengaluru, Remote India" className={inputCls} data-testid="builder-locations-input" />
              </Field>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-[11px] font-mono uppercase tracking-wider text-slate-500">Compensation (optional — never printed on your resume)</p>
              <p className="mt-1 text-xs text-slate-500">We use this only to show roles in your range on the Career Intelligence page.</p>
              <div className="mt-3 grid gap-4 sm:grid-cols-4">
                <Field label="Currency" testId="builder-field-currency">
                  <select value={a.ctc_currency} onChange={(e) => set("ctc_currency", e.target.value)} className={selectCls} data-testid="builder-ctc-currency-select">
                    {cfg.currencies.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Current CTC" testId="builder-field-current-ctc">
                  <Input value={a.current_ctc} onChange={(e) => set("current_ctc", e.target.value)} placeholder="e.g. 42,00,000" className={inputCls} data-testid="builder-current-ctc-input" />
                </Field>
                <Field label="Expected CTC" testId="builder-field-expected-ctc">
                  <Input value={a.expected_ctc} onChange={(e) => set("expected_ctc", e.target.value)} placeholder="e.g. 55,00,000" className={inputCls} data-testid="builder-expected-ctc-input" />
                </Field>
                <Field label="Notice period" testId="builder-field-notice">
                  <select value={a.notice_period} onChange={(e) => set("notice_period", e.target.value)} className={selectCls} data-testid="builder-notice-select">
                    <option value="">Choose…</option>
                    {cfg.notice_options.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </Field>
              </div>
            </div>
          </>
        )}

        {section.id === "about" && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Full name" testId="builder-field-name"><Input value={a.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="As it should appear on the resume" className={inputCls} data-testid="builder-name-input" /></Field>
            <Field label="Email" testId="builder-field-email"><Input value={a.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" className={inputCls} data-testid="builder-email-input" /></Field>
            <Field label="Phone" testId="builder-field-phone"><Input value={a.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+91 …" className={inputCls} data-testid="builder-phone-input" /></Field>
            <Field label="City, country" testId="builder-field-city"><Input value={a.city} onChange={(e) => set("city", e.target.value)} placeholder="Bengaluru, India" className={inputCls} data-testid="builder-city-input" /></Field>
            <Field label="LinkedIn (optional)" testId="builder-field-linkedin"><Input value={a.linkedin} onChange={(e) => set("linkedin", e.target.value)} placeholder="linkedin.com/in/…" className={inputCls} data-testid="builder-linkedin-input" /></Field>
          </div>
        )}

        {section.id === "experience" && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Add up to {cfg.max_positions} positions, newest first. Write achievements in your own words — we polish the wording, never the facts.</p>
            {a.positions.map((p, i) => (
              <div key={i} className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid={`builder-position-${i}`}>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-slate-800">Position {i + 1}</p>
                  {a.positions.length > 1 && (
                    <button onClick={() => set("positions", a.positions.filter((_, j) => j !== i))} className="text-slate-400 hover:text-rose-500" data-testid={`builder-remove-position-${i}`}><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
                <div className="mt-3 grid gap-4 sm:grid-cols-2">
                  <Field label="Company" testId={`builder-position-company-${i}`}><Input value={p.company} onChange={(e) => setPos(i, "company", e.target.value)} className={inputCls} data-testid={`builder-company-input-${i}`} /></Field>
                  <Field label="Job title" testId={`builder-position-title-${i}`}><Input value={p.title} onChange={(e) => setPos(i, "title", e.target.value)} className={inputCls} data-testid={`builder-title-input-${i}`} /></Field>
                  <Field label="Start date" testId={`builder-position-start-${i}`}><DateField value={p.start_date} onChange={(v) => setPos(i, "start_date", v)} testId={`builder-start-date-${i}`} placeholder="Pick start date" /></Field>
                  <div>
                    {p.is_current ? (
                      <Field label="End date" testId={`builder-position-end-${i}`}><p className="rounded-xl border border-slate-200 bg-slate-100 px-3 py-2.5 text-sm text-slate-500">Present</p></Field>
                    ) : (
                      <Field label="End date" testId={`builder-position-end-${i}`}><DateField value={p.end_date} onChange={(v) => setPos(i, "end_date", v)} testId={`builder-end-date-${i}`} placeholder="Pick end date" /></Field>
                    )}
                    <label className="mt-2 inline-flex items-center gap-2 text-xs text-slate-600">
                      <input type="checkbox" checked={p.is_current} onChange={(e) => setPos(i, "is_current", e.target.checked)} className="h-4 w-4" data-testid={`builder-current-checkbox-${i}`} />
                      I still work here
                    </label>
                  </div>
                </div>
                <Field label={`Achievements (up to ${cfg.max_achievements}, one per line)`} testId={`builder-position-achievements-${i}`}>
                  <Textarea rows={4} value={listToText(p.achievements)} onChange={(e) => setPos(i, "achievements", e.target.value.split("\n").slice(0, cfg.max_achievements))}
                    placeholder={"Delivered X, cutting cost by 18%\nLed a team of 12 across 3 countries"} className={textareaCls} data-testid={`builder-achievements-input-${i}`} />
                </Field>
              </div>
            ))}
            {a.positions.length < cfg.max_positions && (
              <Button variant="outline" onClick={() => set("positions", [...a.positions, emptyPosition()])} className="border-slate-200 text-slate-700" data-testid="builder-add-position"><Plus className="w-4 h-4 mr-1.5" /> Add another position</Button>
            )}
          </div>
        )}

        {section.id === "education" && (
          <div className="grid gap-4">
            <Field label="Education (one per line)" hint="e.g. B.Tech, Computer Science — NIT Rourkela, 2008" testId="builder-field-education">
              <Textarea rows={3} {...lineField("education")} className={textareaCls} data-testid="builder-education-input" />
            </Field>
            <Field label="Certifications (one per line)" hint="e.g. AWS Solutions Architect — Professional, 2023" testId="builder-field-certifications">
              <Textarea rows={3} {...lineField("certifications")} className={textareaCls} data-testid="builder-certifications-input" />
            </Field>
          </div>
        )}

        {section.id === "skills" && (
          <div className="space-y-4">
            {!!suggestedSkills.length && (
              <div className="rounded-xl border border-sky-200 bg-sky-50 p-4" data-testid="builder-suggested-skills">
                <p className="text-[11px] font-mono uppercase tracking-wider text-sky-700">Common for {selectedRole?.title} — tap the ones that are true for you</p>
                <div className="mt-2.5 flex flex-wrap gap-1.5">
                  {suggestedSkills.map((s) => (
                    <button key={s} onClick={() => set("skills", [...a.skills, s])} className="rounded-full border border-sky-200 bg-white px-2.5 py-1 text-xs font-medium text-sky-700 hover:bg-sky-100" data-testid={`builder-suggest-skill-${s.replace(/\s+/g, "-").toLowerCase()}`}>+ {s}</button>
                  ))}
                </div>
              </div>
            )}
            <Field label="Your skills" testId="builder-field-skills">
              <div className="flex gap-2">
                <Input value={skillInput} onChange={(e) => setSkillInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && skillInput.trim()) { e.preventDefault(); set("skills", [...new Set([...a.skills, skillInput.trim()])]); setSkillInput(""); } }}
                  placeholder="Type a skill and press Enter" className={inputCls} data-testid="builder-skill-input" />
                <Button onClick={() => { if (skillInput.trim()) { set("skills", [...new Set([...a.skills, skillInput.trim()])]); setSkillInput(""); } }} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="builder-add-skill">Add</Button>
              </div>
            </Field>
            {!!a.skills.length && <Chips items={a.skills} onRemove={(s) => set("skills", a.skills.filter((x) => x !== s))} testId="builder-skill-chips" />}
            <Field label="Tools & platforms (one per line)" testId="builder-field-tools">
              <Textarea rows={3} {...lineField("tools")} placeholder={"Azure\nTerraform\nJira"} className={textareaCls} data-testid="builder-tools-input" />
            </Field>
          </div>
        )}

        {section.id === "extras" && (
          <div className="grid gap-4">
            <Field label="Languages (one per line)" testId="builder-field-languages">
              <Textarea rows={2} {...lineField("languages")} className={textareaCls} data-testid="builder-languages-input" />
            </Field>
            <Field label="Awards, publications, patents (one per line)" testId="builder-field-awards">
              <Textarea rows={3} {...lineField("awards")} className={textareaCls} data-testid="builder-awards-input" />
            </Field>
            <Field label="Anything else we should know?" hint="Context about a career break, relocation, domain focus…" testId="builder-field-notes">
              <Textarea rows={3} value={a.extra_notes} onChange={(e) => set("extra_notes", e.target.value)} className={textareaCls} data-testid="builder-notes-input" />
            </Field>
          </div>
        )}

        {section.id === "review" && (
          <div className="space-y-4">
            {!draft ? (
              <>
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="builder-review-summary">
                  <p className="text-[11px] font-mono uppercase tracking-wider text-slate-500">What we'll write from</p>
                  <ul className="mt-2 space-y-1.5 text-sm text-slate-700">
                    <li>Target: {selectedRole?.title || a.target_role_other || "—"}{a.seniority ? ` · ${a.seniority}` : ""}{a.years_experience ? ` · ${a.years_experience} yrs` : ""}</li>
                    <li>{a.positions.filter((p) => p.company).length} position(s) · {a.skills.length} skill(s) · {a.education.length} education entr{a.education.length === 1 ? "y" : "ies"}</li>
                    <li>Allowance: {cfg.limits.resumes_left} built resume(s) left on your {cfg.limits.tier} plan · {cfg.limits.regenerations} AI rewrites per resume</li>
                  </ul>
                </div>
                <Button onClick={build} disabled={busy} className="h-12 w-full bg-sky-600 hover:bg-sky-700 text-white" data-testid="builder-generate-button">
                  {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Writing your resume…</> : <><Sparkles className="w-4 h-4 mr-2" /> Build my resume</>}
                </Button>
              </>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-2" data-testid="builder-draft-actions">
                  <Button onClick={regenerate} disabled={busy || regensLeft === 0} variant="outline" className="border-slate-200 text-slate-700 disabled:opacity-50" data-testid="builder-regenerate-button">
                    <RefreshCw className="w-4 h-4 mr-1.5" /> AI rewrite {regensLeft != null ? `(${regensLeft} left)` : ""}
                  </Button>
                  <span className="inline-flex items-center gap-1.5 text-xs text-slate-500"><Pencil className="w-3.5 h-3.5" /> Every field below is editable by hand</span>
                </div>
                {regensLeft === 0 && <p className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700" data-testid="builder-regen-exhausted">You've used all AI rewrites for this resume. Fine-tune the wording by hand below — your resume is ready to use.</p>}

                <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="builder-template-picker">
                  <p className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5"><LayoutTemplate className="w-3.5 h-3.5" /> Choose your template</p>
                  {!templates.length ? (
                    <p className="text-sm text-slate-500" data-testid="builder-templates-loading">Loading templates available on your plan…</p>
                  ) : (
                    <div className="flex flex-wrap gap-3">
                      {templates.map((t) => (
                        <button key={t.id} onClick={() => pickTemplate(t.id)} title={t.name}
                          className={`group rounded-lg p-1.5 border transition-colors ${a.preferred_template === t.id ? "border-sky-300 bg-sky-50 ring-2 ring-sky-200" : "border-slate-200 hover:border-slate-300"}`}
                          data-testid={`builder-template-option-${t.id}`}>
                          <TemplateThumbnail tpl={t} />
                          <span className={`block text-center text-[11px] mt-1 ${a.preferred_template === t.id ? "text-sky-700" : "text-slate-500"}`}>{t.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {activeTpl && (
                  <div data-testid="builder-template-preview">
                    <p className="mb-2 text-[11px] font-mono uppercase tracking-wider text-slate-500">Preview · {activeTpl.name}</p>
                    <TemplatedResumePreview data={res} tpl={activeTpl} />
                  </div>
                )}

                <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-4" data-testid="builder-draft-editor">
                  <Field label="Name" testId="builder-edit-name"><Input value={res.name || ""} onChange={(e) => saveEdits({ ...res, name: e.target.value })} className={inputCls} data-testid="builder-edit-name-input" /></Field>
                  <Field label="Target title" testId="builder-edit-title"><Input value={res.target_title || ""} onChange={(e) => saveEdits({ ...res, target_title: e.target.value })} className={inputCls} data-testid="builder-edit-title-input" /></Field>
                  <Field label="Professional summary" testId="builder-edit-summary"><Textarea rows={4} value={res.summary || ""} onChange={(e) => saveEdits({ ...res, summary: e.target.value })} className={textareaCls} data-testid="builder-edit-summary-input" /></Field>
                  <Field label="Skills (comma separated)" testId="builder-edit-skills"><Input value={raw.__editskills ?? (res.skills || []).join(", ")} onChange={(e) => { setRaw((r) => ({ ...r, __editskills: e.target.value })); saveEdits({ ...res, skills: e.target.value.split(",") }); }} className={inputCls} data-testid="builder-edit-skills-input" /></Field>
                  {(res.experience || []).map((job, i) => (
                    <div key={i} className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid={`builder-edit-experience-${i}`}>
                      <p className="text-sm font-semibold text-slate-800">{job.title} · {job.company} <span className="font-normal text-slate-500">({job.duration})</span></p>
                      <Textarea rows={Math.max(3, (job.bullets || []).length + 1)} value={listToText(job.bullets)}
                        onChange={(e) => saveEdits({ ...res, experience: res.experience.map((x, j) => (j === i ? { ...x, bullets: e.target.value.split("\n") } : x)) })}
                        className={`mt-2 ${textareaCls}`} data-testid={`builder-edit-bullets-${i}`} />
                    </div>
                  ))}
                </div>

                <Button onClick={finalize} disabled={busy} className="h-12 w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="builder-finalize-button">
                  {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analysing…</> : <><FileText className="w-4 h-4 mr-2" /> Use this resume & open Career Intelligence</>}
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {section.id !== "review" && (
        <div className="flex items-center justify-between gap-2">
          <Button variant="outline" onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={idx === 0} className="h-11 border-slate-200 text-slate-600 disabled:opacity-40" data-testid="builder-back-button">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
          </Button>
          <span className="num-display text-xs text-slate-500">{idx + 1} / {SECTIONS.length}</span>
          <Button onClick={() => setIdx((i) => i + 1)} disabled={!canNext()} className="h-11 bg-sky-600 hover:bg-sky-700 text-white disabled:opacity-40" data-testid="builder-next-button">
            Next <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default ResumeBuilderWizard;
